import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { X, Camera, FileText, Link as LinkIcon, Plus, Trash2, ExternalLink, Loader2, ShieldCheck, Printer } from 'lucide-react';
import { toast } from 'sonner';
import SaveSnapshotButton from './SaveSnapshotButton';
import { printHtml } from '../lib/print';
import DocumentHeader from './DocumentHeader';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND = process.env.REACT_APP_BACKEND_URL;

/* Insurer-Assets-for-Disposal — assets the insurance carrier has approved
   for write-off. Singleton doc with three lists (photos, documents, links).
   Public read; admin/engineer only can edit. */
export default function InsurerAssetsDialog({ open, onClose, hasFullAccess, adminToken }) {
  const [tab, setTab] = useState('photos'); // photos | documents | links
  const [data, setData] = useState({ photos: [], documents: [], links: [], notes: '' });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [newLink, setNewLink] = useState({ label: '', url: '', note: '' });
  const [docName, setDocName] = useState('Insurer Assets Register');

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/insurer-assets`);
      setData({
        photos: data.photos || [],
        documents: data.documents || [],
        links: data.links || [],
        notes: data.notes || '',
      });
    } catch (e) {
      console.error('Failed to load insurer assets', e);
    }
  }, []);

  useEffect(() => { if (open) load(); }, [open, load]);

  const persist = async (next) => {
    if (!hasFullAccess) return; // viewers/editors are read-only
    setSaving(true);
    try {
      await axios.put(`${API}/admin/insurer-assets`, next, {
        headers: { Authorization: `Bearer ${adminToken}` },
        withCredentials: true,
      });
      setData(next);
      toast.success('Saved');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const upload = async (file, kind) => {
    if (!hasFullAccess) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      // Keep PDFs raw so the photo gallery can render a real PDF tile
      // rather than a first-page PNG conversion.
      const isPdf = /^application\/pdf$/i.test(file.type || '') || /\.pdf$/i.test(file.name || '');
      if (isPdf) fd.append('keep_raw', 'true');
      const { data: up } = await axios.post(`${API}/admin/upload`, fd, {
        headers: { Authorization: `Bearer ${adminToken}`, 'Content-Type': 'multipart/form-data' },
        withCredentials: true,
      });
      const url = up.url.startsWith('http') ? up.url : `${BACKEND}${up.url}`;
      const next = { ...data, [kind]: [...(data[kind] || []), url] };
      await persist(next);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const remove = async (kind, idx) => {
    if (!hasFullAccess) return;
    if (!window.confirm('Remove this entry?')) return;
    const next = { ...data, [kind]: data[kind].filter((_, i) => i !== idx) };
    await persist(next);
  };

  const addLink = async () => {
    if (!hasFullAccess) return;
    const url = (newLink.url || '').trim();
    const label = (newLink.label || '').trim() || url;
    if (!url) { toast.error('Paste a URL'); return; }
    if (!/^https?:\/\//i.test(url)) { toast.error('URL must start with http:// or https://'); return; }
    const next = { ...data, links: [...(data.links || []), { label, url, note: newLink.note || '' }] };
    await persist(next);
    setNewLink({ label: '', url: '', note: '' });
  };

  if (!open) return null;

  return (
    <div data-testid="insurer-assets-dialog" className="fixed inset-0 z-[100] flex items-center justify-center p-4" style={{ backgroundColor: 'rgba(15,23,42,0.65)', backdropFilter: 'blur(8px)' }}>
      <div className="w-full max-w-5xl max-h-[90vh] flex flex-col rounded-2xl overflow-hidden shadow-2xl" style={{ backgroundColor: '#FAF8F2' }}>
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b" style={{ backgroundColor: '#0E1F2C', borderColor: '#1F2D3D' }}>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: '#FDE68A' }}>
              <ShieldCheck className="w-5 h-5" style={{ color: '#1A2E2E' }} />
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-[0.2em] font-bold" style={{ color: '#FDE68A', fontFamily: 'Manrope' }}>Insurer-Approved Disposal</p>
              <h2 className="text-lg font-bold text-white" style={{ fontFamily: 'Manrope' }}>Assets Approved for Disposal</h2>
            </div>
          </div>
          <button data-testid="insurer-assets-close" onClick={onClose} className="p-2 rounded-lg text-white hover:bg-white/10 transition">
            <X className="w-5 h-5" />
          </button>
        </div>

        {hasFullAccess && (
          <div className="flex items-center justify-end gap-2 px-5 py-2 border-b" style={{ borderColor: '#E2DBC8', backgroundColor: '#F7F2E8' }}>
            <button
              type="button"
              data-testid="insurer-print"
              title="Print this insurer assets record"
              onClick={() => {
                const esc = (v) => String(v ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
                const photos = (data.photos || []).map((u) => `<li>${esc((u || '').split('/').pop())}</li>`).join('');
                const docs = (data.documents || []).map((u) => `<li>${esc((u || '').split('/').pop())}</li>`).join('');
                const links = (data.links || []).map((l) => { const url = typeof l === 'string' ? l : (l?.url || ''); const label = typeof l === 'object' ? (l.label || url) : url; return `<li>${esc(label)}</li>`; }).join('');
                const body = `
                  <p class="kicker">DREAMS ROSE HALL · INSURER ASSETS</p>
                  <h1>${esc(docName || 'Insurer Assets Register')}</h1>
                  <p class="meta">${data.photos.length} photo${data.photos.length === 1 ? '' : 's'} · ${data.documents.length} document${data.documents.length === 1 ? '' : 's'} · ${data.links.length} link${data.links.length === 1 ? '' : 's'} · Printed ${esc(new Date().toLocaleString())}</p>
                  ${data.notes ? `<h2>Notes</h2><p>${esc(data.notes).replace(/\n/g, '<br/>')}</p>` : ''}
                  ${photos ? `<h2>Photos</h2><ul>${photos}</ul>` : ''}
                  ${docs ? `<h2>Documents</h2><ul>${docs}</ul>` : ''}
                  ${links ? `<h2>Links</h2><ul>${links}</ul>` : ''}
                `;
                printHtml(body, docName || 'Insurer Assets Register');
              }}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-bold text-white"
              style={{ backgroundColor: '#0A1A2F', fontFamily: 'Manrope' }}
            >
              <Printer className="w-3 h-3" /> Print
            </button>
            <SaveSnapshotButton
              kind="insurer"
              defaultTitle={docName || `Insurer Assets · ${new Date().toLocaleDateString()}`}
              getPayload={() => ({ ...data, doc_name: docName })}
              testid="insurer-save-snapshot"
            />
          </div>
        )}

        {/* Branded centered banner + editable document name. */}
        {hasFullAccess && (
          <div className="px-5 pt-3">
            <DocumentHeader
              scopeId="insurer-assets"
              autoName="Insurer Assets Register"
              onChange={setDocName}
              readOnly={!hasFullAccess}
            />
          </div>
        )}

        {/* Totals summary */}
        <div
          data-testid="insurer-totals"
          className="mx-5 mt-2 mb-1 p-3 rounded-xl flex flex-wrap items-center gap-4 text-xs"
          style={{ backgroundColor: '#FEF3C7', border: '1px solid #FDE68A', fontFamily: 'Manrope' }}
        >
          <span className="font-bold uppercase tracking-wider text-[10px]" style={{ color: '#92400E' }}>Totals</span>
          <span style={{ color: '#1A2E2E' }}>Photos: <strong>{data.photos.length}</strong></span>
          <span style={{ color: '#1A2E2E' }}>Documents: <strong>{data.documents.length}</strong></span>
          <span style={{ color: '#1A2E2E' }}>Links: <strong>{data.links.length}</strong></span>
          <span className="ml-auto px-2 py-0.5 rounded-full font-bold" style={{ backgroundColor: '#92400E', color: 'white' }}>
            {data.photos.length + data.documents.length + data.links.length} total assets
          </span>
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-1 px-4 pt-3 border-b" style={{ borderColor: '#E2DBC8' }}>
          {[
            { id: 'photos',    label: `Photos (${data.photos.length})`,       icon: Camera },
            { id: 'documents', label: `Documents (${data.documents.length})`, icon: FileText },
            { id: 'links',     label: `Shared Links (${data.links.length})`,  icon: LinkIcon },
          ].map((t) => (
            <button
              key={t.id}
              data-testid={`insurer-tab-${t.id}`}
              onClick={() => setTab(t.id)}
              className="px-4 py-2.5 text-xs font-bold rounded-t-lg flex items-center gap-1.5 transition"
              style={{
                backgroundColor: tab === t.id ? '#FAF8F2' : 'transparent',
                color: tab === t.id ? '#003DA5' : '#5C6B6B',
                borderBottom: tab === t.id ? '3px solid #003DA5' : '3px solid transparent',
                fontFamily: 'Manrope',
              }}
            >
              <t.icon className="w-3.5 h-3.5" /> {t.label}
            </button>
          ))}
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6">
          {!hasFullAccess && (
            <div className="mb-4 p-3 rounded-lg text-xs" style={{ backgroundColor: '#FEF3C7', color: '#92400E', fontFamily: 'Manrope' }}>
              You're viewing this in read-only mode. Sign in as an admin or engineer to upload, link, or remove assets.
            </div>
          )}

          {tab === 'photos' && (
            <PhotoTab
              photos={data.photos}
              hasFullAccess={hasFullAccess}
              uploading={uploading}
              onUpload={(f) => upload(f, 'photos')}
              onRemove={(i) => remove('photos', i)}
            />
          )}
          {tab === 'documents' && (
            <DocumentsTab
              documents={data.documents}
              hasFullAccess={hasFullAccess}
              uploading={uploading}
              onUpload={(f) => upload(f, 'documents')}
              onRemove={(i) => remove('documents', i)}
            />
          )}
          {tab === 'links' && (
            <LinksTab
              links={data.links}
              hasFullAccess={hasFullAccess}
              newLink={newLink}
              setNewLink={setNewLink}
              onAdd={addLink}
              onRemove={(i) => remove('links', i)}
            />
          )}
        </div>

        {saving && (
          <div className="px-4 py-2 text-[11px] flex items-center gap-2" style={{ backgroundColor: '#0F766E', color: 'white', fontFamily: 'Manrope' }}>
            <Loader2 className="w-3.5 h-3.5 animate-spin" /> Saving…
          </div>
        )}
      </div>
    </div>
  );
}

function PhotoTab({ photos, hasFullAccess, uploading, onUpload, onRemove }) {
  return (
    <div>
      {hasFullAccess && (
        <UploadButton
          accept="image/*,video/*,.pdf,application/pdf,.xls,.xlsx,.csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
          uploading={uploading}
          label="Add photo / PDF / Excel"
          testid="insurer-upload-photo"
          onPick={onUpload}
        />
      )}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
        {photos.map((u) => {
          const lower = (u || '').toLowerCase();
          const isPdf = /\.pdf(\?|$)/i.test(lower);
          const isExcel = /\.(xlsx?|csv)(\?|$)/i.test(lower);
          const isFile = isPdf || isExcel;
          const fileLabel = isPdf ? 'PDF' : isExcel ? 'EXCEL' : '';
          const fileColor = isPdf ? '#DC2626' : '#16A34A';
          const fileBg = isPdf ? '#FDECEC' : '#ECFDF5';
          return (
            <div key={u} className="relative group rounded-xl overflow-hidden aspect-square border" style={{ borderColor: '#E2DBC8', backgroundColor: isFile ? fileBg : 'transparent' }}>
              {isFile ? (
                <a href={u} target="_blank" rel="noreferrer" className="w-full h-full flex flex-col items-center justify-center gap-1 text-center p-2" style={{ color: fileColor, fontFamily: 'Manrope' }}>
                  <FileText className="w-7 h-7" />
                  <span className="text-[10px] font-bold uppercase tracking-wider">{fileLabel}</span>
                  <span className="text-[9px] font-semibold truncate max-w-full" style={{ color: '#1A2E2E' }}>
                    {decodeURIComponent(u.split('/').pop().split('?')[0])}
                  </span>
                </a>
              ) : (
                <img src={u} alt="Insurer asset" className="w-full h-full object-cover" />
              )}
              {hasFullAccess && (
                <button
                  data-testid={`insurer-photo-remove-${photos.indexOf(u)}`}
                  onClick={() => onRemove(photos.indexOf(u))}
                  className="absolute top-2 right-2 p-1.5 rounded-full opacity-0 group-hover:opacity-100 transition"
                  style={{ backgroundColor: 'rgba(220,38,38,0.95)', color: 'white' }}
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          );
        })}
        {photos.length === 0 && (
          <div className="col-span-full text-center py-12 text-sm" style={{ color: '#5C6B6B', fontFamily: 'Manrope' }}>
            No photos uploaded yet.
          </div>
        )}
      </div>
    </div>
  );
}

function DocumentsTab({ documents, hasFullAccess, uploading, onUpload, onRemove }) {
  return (
    <div>
      {hasFullAccess && (
        <UploadButton
          accept=".pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,.zip"
          uploading={uploading}
          label="Add document"
          testid="insurer-upload-document"
          onPick={onUpload}
        />
      )}
      <ul className="divide-y rounded-xl border" style={{ borderColor: '#E2DBC8', backgroundColor: 'white' }}>
        {documents.map((u, idx) => {
          const fname = decodeURIComponent(u.split('/').pop().split('?')[0]);
          return (
            <li key={u} className="flex items-center justify-between px-4 py-3 hover:bg-amber-50/50">
              <a href={u} target="_blank" rel="noreferrer" className="flex items-center gap-3 text-sm flex-1 min-w-0" style={{ fontFamily: 'Manrope', color: '#1A2E2E' }}>
                <FileText className="w-4 h-4 flex-shrink-0" style={{ color: '#003DA5' }} />
                <span className="truncate">{fname}</span>
                <ExternalLink className="w-3 h-3 ml-auto opacity-60" />
              </a>
              {hasFullAccess && (
                <button
                  data-testid={`insurer-doc-remove-${idx}`}
                  onClick={() => onRemove(idx)}
                  className="ml-3 p-1.5 rounded text-red-600 hover:bg-red-50"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              )}
            </li>
          );
        })}
        {documents.length === 0 && (
          <li className="text-center py-12 text-sm" style={{ color: '#5C6B6B', fontFamily: 'Manrope' }}>
            No documents uploaded yet.
          </li>
        )}
      </ul>
    </div>
  );
}

function LinksTab({ links, hasFullAccess, newLink, setNewLink, onAdd, onRemove }) {
  return (
    <div>
      {hasFullAccess && (
        <div className="mb-4 p-4 rounded-xl border" style={{ backgroundColor: 'white', borderColor: '#E2DBC8' }}>
          <p className="text-[10px] uppercase tracking-[0.15em] font-bold mb-2" style={{ color: '#003DA5', fontFamily: 'Manrope' }}>Add a shared-drive / cloud link</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
            <input
              data-testid="insurer-link-label"
              value={newLink.label}
              onChange={(e) => setNewLink((s) => ({ ...s, label: e.target.value }))}
              placeholder="Label (e.g. Q3 inventory)"
              className="px-3 py-2 rounded-lg text-sm border"
              style={{ borderColor: '#D6D0C0', fontFamily: 'Manrope' }}
            />
            <input
              data-testid="insurer-link-url"
              value={newLink.url}
              onChange={(e) => setNewLink((s) => ({ ...s, url: e.target.value }))}
              placeholder="https://drive.google.com/…"
              className="px-3 py-2 rounded-lg text-sm border md:col-span-2"
              style={{ borderColor: '#D6D0C0', fontFamily: 'Manrope' }}
            />
          </div>
          <div className="mt-2 flex items-center gap-2">
            <input
              data-testid="insurer-link-note"
              value={newLink.note}
              onChange={(e) => setNewLink((s) => ({ ...s, note: e.target.value }))}
              placeholder="Optional note (who shared it, expiry, etc.)"
              className="flex-1 px-3 py-2 rounded-lg text-sm border"
              style={{ borderColor: '#D6D0C0', fontFamily: 'Manrope' }}
            />
            <button
              data-testid="insurer-link-add"
              onClick={onAdd}
              className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-bold text-white"
              style={{ backgroundColor: '#003DA5', fontFamily: 'Manrope' }}
            >
              <Plus className="w-4 h-4" /> Add
            </button>
          </div>
        </div>
      )}
      <ul className="divide-y rounded-xl border" style={{ borderColor: '#E2DBC8', backgroundColor: 'white' }}>
        {links.map((lk, idx) => (
          <li key={`${lk.url}-${idx}`} className="flex items-center justify-between px-4 py-3 hover:bg-amber-50/50">
            <a href={lk.url} target="_blank" rel="noreferrer" className="flex-1 min-w-0" style={{ fontFamily: 'Manrope' }}>
              <p className="text-sm font-bold truncate" style={{ color: '#1A2E2E' }}>{lk.label || lk.url}</p>
              <p className="text-[11px] truncate" style={{ color: '#5C6B6B' }}>{lk.url}</p>
              {lk.note && <p className="text-[11px] mt-0.5" style={{ color: '#7A8C8C' }}>{lk.note}</p>}
            </a>
            <ExternalLink className="w-3.5 h-3.5 ml-3 opacity-60" />
            {hasFullAccess && (
              <button
                data-testid={`insurer-link-remove-${idx}`}
                onClick={() => onRemove(idx)}
                className="ml-2 p-1.5 rounded text-red-600 hover:bg-red-50"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            )}
          </li>
        ))}
        {links.length === 0 && (
          <li className="text-center py-12 text-sm" style={{ color: '#5C6B6B', fontFamily: 'Manrope' }}>
            No shared links yet.
          </li>
        )}
      </ul>
    </div>
  );
}

function UploadButton({ accept, uploading, label, testid, onPick }) {
  return (
    <label
      className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-bold text-white cursor-pointer mb-4 transition hover:-translate-y-0.5"
      style={{ backgroundColor: '#003DA5', fontFamily: 'Manrope', boxShadow: '0 2px 8px rgba(0,61,165,0.25)' }}
    >
      <input
        data-testid={testid}
        type="file"
        accept={accept}
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) onPick(file);
          e.target.value = '';
        }}
      />
      {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
      {uploading ? 'Uploading…' : label}
    </label>
  );
}
